import React, { useState, useEffect } from 'react';
import { savedCompaniesApi } from '../../../services/prospector/savedCompanies';
import type { SavedCompanyList } from '../../../types/prospector/savedCompanies.types';
import { industryCodes } from '../../../data/prospector/industryCodes';

interface BulkSaveComponentProps {
  companiesCount: number;
  onSave: (listId: number) => void;
  onCancel: () => void;
  isLoading: boolean;
  searchParams?: any; // Search parameters to generate suggested list name
}

export default function BulkSaveComponent({ 
  companiesCount, 
  onSave, 
  onCancel, 
  isLoading,
  searchParams 
}: BulkSaveComponentProps) {
  const [lists, setLists] = useState<SavedCompanyList[]>([]);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Find industry name by code
  const findIndustryName = (code: string): string | null => {
    // Search through all levels of industry codes
    for (const level of Object.values(industryCodes)) {
      for (const industry of level) {
        if (industry.code === code) {
          return industry.shortName || industry.name;
        }
        // Search in children if they exist
        if (industry.children) {
          const findInChildren = (children: any[]): string | null => {
            for (const child of children) {
              if (child.code === code) {
                return child.shortName || child.name;
              }
              if (child.children) {
                const found = findInChildren(child.children);
                if (found) return found;
              }
            }
            return null;
          };
          const found = findInChildren(industry.children);
          if (found) return found;
        }
      }
    }
    return null;
  };

  // Generate suggested list name based on search parameters
  const generateSuggestedListName = () => {
    if (!searchParams) return '';
    
    const parts: string[] = [];
    
    // Add industry description if available
    if (searchParams.naeringskode) {
      // The naeringskode might be a comma-separated list, take the first one
      const codes = searchParams.naeringskode.split(',');
      const firstCode = codes[0].trim();
      
      // Look up industry description from the actual data
      const industryName = findIndustryName(firstCode);
      if (industryName) {
        parts.push(industryName);
      } else {
        // Fallback to the code itself
        parts.push(`Næring ${firstCode}`);
      }
    }
    
    // Add employee count range if available
    if (searchParams.antallAnsatte) {
      if (searchParams.antallAnsatte === '250+') {
        parts.push('250+ ansatte');
      } else {
        parts.push(`${searchParams.antallAnsatte} ansatte`);
      }
    } else if (searchParams.fraAntallAnsatte && searchParams.tilAntallAnsatte) {
      parts.push(`${searchParams.fraAntallAnsatte}-${searchParams.tilAntallAnsatte} ansatte`);
    } else if (searchParams.fraAntallAnsatte) {
      parts.push(`${searchParams.fraAntallAnsatte}+ ansatte`);
    }
    
    // Add location if available
    if (searchParams.kommunenummer) {
      // You could add a lookup for kommune names
      parts.push(`Kommune ${searchParams.kommunenummer}`);
    }
    
    // Add company name search if it's specific
    if (searchParams.navn && searchParams.navn.length > 2) {
      parts.push(`"${searchParams.navn}"`);
    }
    
    return parts.length > 0 ? parts.join(', ') : '';
  };

  // Fetch user's lists on component mount
  useEffect(() => {
    const fetchLists = async () => {
      setLoadingLists(true);
      try {
        const userLists = await savedCompaniesApi.getLists();
        setLists(userLists);
        // Select the first list by default if any exist
        if (userLists.length > 0) {
          setSelectedListId(userLists[0].id || null);
        }
      } catch (error) {
        console.error('Error fetching company lists:', error);
        setError('Could not fetch company lists');
      } finally {
        setLoadingLists(false);
      }
    };

    fetchLists();
  }, []);

  // Handle creating a new list
  const handleCreateList = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newListName.trim()) {
      setError('Please enter a name for the list');
      return;
    }

    try {
      const newList = await savedCompaniesApi.createList({
        name: newListName,
        description: newListDescription
      });

      setLists([...lists, newList]);
      setSelectedListId(newList.id || null);
      setNewListName('');
      setNewListDescription('');
      setShowNewListForm(false);
      setError(null);
    } catch (error) {
      console.error('Error creating new list:', error);
      setError('Could not create new list');
    }
  };

  const handleSave = () => {
    if (!selectedListId) {
      setError('Please select a list');
      return;
    }
    onSave(selectedListId);
  };

  return (
    <div className="space-y-3">
      {error && (
        <div className="p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      {!showNewListForm ? (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select destination list
            </label>
            <select
              value={selectedListId || ''}
              onChange={(e) => setSelectedListId(Number(e.target.value))}
              className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white text-sm"
              disabled={loadingLists || lists.length === 0}
            >
              {lists.length === 0 && <option value="">No lists available</option>}
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name} ({list.company_count || 0} companies)
                </option>
              ))}
            </select>
          </div>

          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading || !selectedListId || loadingLists}
              className={`flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                isLoading || !selectedListId || loadingLists
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none'
              }`}
            >
              {isLoading ? 'Saving...' : `Save ${companiesCount} Companies`}
            </button>
            
            <button
              type="button"
              onClick={() => {
                setShowNewListForm(true);
                // Auto-populate with suggested name
                const suggested = generateSuggestedListName();
                if (suggested) {
                  setNewListName(suggested);
                }
              }}
              disabled={isLoading}
              className="py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              + New List
            </button>
            
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={handleCreateList} className="space-y-3">
          <div>
            <label htmlFor="newListName" className="block text-sm font-medium text-gray-700 mb-1">
              List name
            </label>
            <input
              type="text"
              id="newListName"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white text-sm"
              placeholder="e.g. Potential customers"
              required
            />
          </div>
          
          <div>
            <label htmlFor="newListDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="newListDescription"
              value={newListDescription}
              onChange={(e) => setNewListDescription(e.target.value)}
              className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white text-sm"
              placeholder="List description"
              rows={2}
            />
          </div>
          
          <div className="flex space-x-2">
            <button
              type="submit"
              disabled={isLoading || !newListName.trim()}
              className={`flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                isLoading || !newListName.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none'
              }`}
            >
              Create List
            </button>
            
            <button
              type="button"
              onClick={() => setShowNewListForm(false)}
              disabled={isLoading}
              className="py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Back
            </button>
          </div>
        </form>
      )}
    </div>
  );
}